import React from "react";
import * as Sentry from "@sentry/react";
import { supabase } from "@/integrations/supabase/client";
import { AlertCircle, RefreshCw } from "lucide-react";
import { Button } from "./ui/button";

/**
 * Composant de fallback pour ErrorBoundary Sentry
 * Affiche une interface utilisateur conviviale en cas d'erreur critique
 * et log également dans Supabase pour backup
 */
export function ErrorBoundary() {
  const handleReload = () => {
    window.location.reload();
  };

  const handleReportFeedback = () => {
    // Ouvre le dialogue de feedback Sentry
    const feedbackButton = document.querySelector('[data-sentry-feedback]');
    if (feedbackButton instanceof HTMLElement) {
      feedbackButton.click();
    }
  };

  // Log aussi dans Supabase en backup (au cas où Sentry échoue)
  const logToSupabase = async (error: Error) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      await supabase.from("error_logs").insert({
        error_message: error.message,
        error_stack: error.stack,
        component_stack: "Sentry ErrorBoundary",
        user_id: user?.id,
        user_agent: navigator.userAgent,
        page_url: window.location.href,
        created_at: new Date().toISOString()
      });
    } catch (logError) {
      // Utiliser le service de logging pour cette erreur aussi
      logError("Failed to log error to Supabase", logError as Error, { component: "ErrorBoundary" });
    }
  };

  // Log automatiquement quand ce composant est affiché
  React.useEffect(() => {
    const lastError = Sentry.lastEventId();
    if (lastError) {
      logToSupabase(new Error("Error caught by Sentry"));
    }
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <div className="max-w-md w-full space-y-4 text-center">
        <AlertCircle className="mx-auto h-16 w-16 text-destructive animate-pulse" />
        
        <div className="space-y-2">
          <h1 className="text-2xl font-bold text-foreground">
            Une erreur est survenue
          </h1>
          <p className="text-muted-foreground">
            L'application a rencontré une erreur inattendue. 
            Nos équipes ont été automatiquement notifiées et travaillent sur une résolution.
          </p>
        </div>

        {import.meta.env.DEV && (
          <div className="text-left p-4 bg-muted rounded-lg border border-border">
            <p className="font-mono text-xs text-muted-foreground">
              Mode développement : Vérifiez la console pour plus de détails
            </p>
          </div>
        )}

        <div className="space-y-2">
          <Button 
            onClick={handleReload} 
            className="w-full"
            size="lg"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Recharger la page
          </Button>
          
          <Button 
            onClick={handleReportFeedback} 
            variant="outline"
            className="w-full"
          >
            Signaler le problème
          </Button>
        </div>

        <p className="text-xs text-muted-foreground">
          ID d'erreur : {Sentry.lastEventId() || "N/A"}
        </p>
      </div>
    </div>
  );
}

// Export également un HOC pour wrapper des composants spécifiques
export const withErrorBoundary = Sentry.withErrorBoundary;
